/**
 * Service with some device functions that do not fit anywhere else
 */
import { UAParser } from "ua-parser-js";
import action from "../actions/bound-action-creators";
import cryptoLibrary from "./crypto-library";

import { getStore } from "./store";

let browserData: UAParser.IResult | undefined;

activate();

function activate(): void {
	if (typeof window === "undefined") {
		//we are in a background script and don't have a window
		return;
	}
	const parser = new UAParser();
	browserData = parser.getResult();
}

/**
 * Returns the device fingerprint
 *
 * @returns {string} Fingerprint of the device
 */
function getDeviceFingerprint(): string {
	let fingerprint = getStore().getState().persistent.fingerprint;
	if (fingerprint == null) {
		fingerprint = cryptoLibrary.generateUuid();
		action().setFingerprint(fingerprint);
	}

	return fingerprint;
}

/**
 * Returns weather we are supposed to show a title bar
 *
 * @returns {boolean} Is this an electron app
 */
function hasTitlebar(): boolean | undefined {
	return isElectron() && (isWindows() || isMac());
}

/**
 * Returns weather we have an electron app
 *
 * @returns {boolean} Is this an electron app
 */
function isElectron(): boolean {
	return TARGET === "electron";
}

/**
 * Returns weather we have a webclient (no electron app
 *
 * @returns {boolean} Is this a webclient
 */
function isWebclient(): boolean {
	return TARGET !== "chrome" && TARGET !== "firefox" && TARGET !== "safari";
}

/**
 * Returns weather we have a windows machine
 *
 * @returns {boolean} Is this a windows device
 */
function isWindows(): boolean | undefined {
	return browserData && /Windows/i.test(browserData.os.name ?? "");
}

/**
 * Returns weather we have a mac
 *
 * @returns {boolean} Is this a mac device
 */
function isMac(): boolean | undefined {
	return browserData && /Mac/i.test(browserData.os.name ?? "");
}

/**
 * Returns weather we have a linux device
 *
 * @returns {boolean} Is this a linux device
 */
function isLinux(): boolean | undefined {
	return browserData && /Linux/i.test(browserData.os.name ?? "");
}

/**
 * Returns weather we have an android device or not
 *
 * @returns {boolean} Is this an android device
 */
function isMobileAndroid(): boolean | undefined {
	return browserData && !!browserData.ua.match(/Android/i);
}

/**
 * Returns weather we have an ios device or not
 *
 * @returns {boolean} Is this an ios device
 */
function isMobileIos(): boolean | undefined {
	return browserData && !!browserData.ua.match(/iPhone|iPad|iPod/i);
}

/**
 * Returns weather we have an ios device or not
 *
 * @returns {boolean} Is this an ios device
 */
function isMobile(): boolean | undefined {
	if (!browserData) {
		return browserData;
	}
	// The legacy detector assumes one of the user-agent strings is present.
	const dataString = (browserData.ua || navigator.vendor || window.opera)!;
	return (
		/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
			dataString,
		) ||
		/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
			dataString.substr(0, 4),
		)
	);
}

/**
 * Returns weather we have a Chrome or not
 *
 * @returns {boolean} Is this a Chrome user
 */
function isChrome(): boolean | undefined {
	return browserData && /Chrome/i.test(browserData.browser.name ?? "");
}

/**
 * Returns weather we have an Edge or not
 *
 * @returns {boolean} Is this an Edge user
 */
function isEdge(): boolean | undefined {
	return browserData && /Edge/i.test(browserData.browser.name ?? "");
}

/**
 * Returns weather we have a Safari or not
 *
 * @returns {boolean} Is this a Safari user
 */
function isSafari(): boolean | undefined {
	return browserData && /Safari/i.test(browserData.browser.name ?? "");
}

/**
 * Returns weather we have a Firefox or not
 *
 * @returns {boolean} Is this an Firefox user
 */
function isFirefox(): boolean | undefined {
	return browserData && /Firefox/i.test(browserData.browser.name ?? "");
}

/**
 * Generates the Device description out of the Vendor, OS, Version and others
 *
 * @returns {string} Returns the device's description
 */
function getDeviceDescription(): string {
	let description = "";
	if (browserData && typeof browserData.device.vendor !== "undefined") {
		description = description + browserData.device.vendor + " ";
	}
	if (browserData && typeof browserData.device.model !== "undefined") {
		description = description + browserData.device.model + " ";
	}
	if (browserData && typeof browserData.os.name !== "undefined") {
		description = description + browserData.os.name + " ";
	}
	if (browserData && typeof browserData.os.version !== "undefined") {
		description = description + browserData.os.version + " ";
	}
	if (browserData && typeof browserData.browser.name !== "undefined") {
		description = description + browserData.browser.name + " ";
	}
	if (browserData && typeof browserData.browser.version !== "undefined") {
		description = description + browserData.browser.version + " ";
	}
	return description;
}

const deviceService = {
	getDeviceFingerprint: getDeviceFingerprint,
	hasTitlebar: hasTitlebar,
	isElectron: isElectron,
	isWebclient: isWebclient,
	isWindows: isWindows,
	isLinux: isLinux,
	isMac: isMac,
	isMobileAndroid: isMobileAndroid,
	isMobileIos: isMobileIos,
	isMobile: isMobile,
	isChrome: isChrome,
	isEdge: isEdge,
	isSafari: isSafari,
	isFirefox: isFirefox,
	getDeviceDescription: getDeviceDescription,
};
export default deviceService;
